@let isModalOpenedOnEdit = (data && data.idForEdit) || item;

<h1 mat-dialog-title>
  {{ data.title }}
</h1>
<form
  trackFocusedOnce
  [formGroup]="addEditForm"
  (ngSubmit)="onFormDialogSubmit()"
  (firstFocus)="addEditForm.isFocusedOnce.next(true)"
  #form="ngForm"
>
  <div mat-dialog-content>
    <div class="row">
      <div class="col-lg-9 col-md-12 left-inputs-container">
        <div [class]="{ 'mb-4': (cronToString$ | async) === null }">
          <div class="d-flex top-buttons-group">
            <button mat-stroked-button tabindex="-1" type="button" [matMenuTriggerFor]="menu">
              <mat-icon>add</mat-icon>
              {{ 'button.add' | translate }}
            </button>
            <button
              mat-stroked-button
              [hidden]="!item?.id || (isSubmitInProgress$ | async)"
              (click)="openEscalateModal()"
              type="button"
              tabindex="-1"
            >
              <mat-icon>arrow_upward</mat-icon>
              {{ 'page.items.escalation.escalate' | translate }}
            </button>
          </div>

          @if (cronToString$ | async) {
            <mat-hint class="text-grey pt-2 pb-075 d-block">{{ cronToString$ | async }}</mat-hint>
          }
        </div>
        <app-read-to-write-input
          class="d-block"
          [formControl]="addEditForm.controls.name"
          [label]="'field.title' | translate"
          [shouldBeStuckInWriteMode]="!isModalOpenedOnEdit"
          [isFormDataLoading]="(store.isDataForEditLoading$ | async) === true"
        ></app-read-to-write-input>
        <app-link-work-instruction
          [boardId]="selectedBoardId"
          [itemId]="item?.id"
          [dueDateForRecurrence]="item?.itemRecurrence ? item?.dueDate : null"
          [loadTrigger]="triggerWorkInstructionLoad"
        ></app-link-work-instruction>
        <app-text-editor
          class="d-block mb-4"
          [formControl]="addEditForm.controls.description"
          [isFormSubmitted]="form.submitted"
          [label]="'field.description' | translate"
          [noDataPlaceholder]="isModalOpenedOnEdit ? ('page.items.label.descriptionPlaceholder' | translate) : null"
          [shouldBeStuckInWriteMode]="!isModalOpenedOnEdit"
          [isFormDataLoading]="(store.isDataForEditLoading$ | async) === true"
        >
        </app-text-editor>

        <app-item-custom-fields
          *ngIf="addEditForm.controls.itemCategory.value?.length && !isDefaultFieldSetSelected"
          [fieldSetAssignments]="fieldSetAssignments"
          [customFieldsForm]="customFieldsForm"
          [addEditForm]="addEditForm"
          [isFormSubmitted]="form.submitted"
          [item]="item"
        ></app-item-custom-fields>
        <div
          *ngIf="
            store.boardItemConfigurationsLoading() !== ELoadStatus.initial &&
            store.boardItemConfigurationsLoading() !== ELoadStatus.loading
          "
        >
          <app-item-readonly-fields
            [fieldSetAssignments]="fieldSetAssignments"
            [isFormSubmitted]="form.submitted"
            [item]="item"
          ></app-item-readonly-fields>
        </div>
        <div class="mb-3">
          <app-link-item
            id="appLinkItem"
            [item]="item"
            [startDateForRecurringUpdate]="isRecurringUpdateAllowed() ? (item?.dueDate ?? null) : null"
            [loadStatus]="linkItemLoadStatusFromComponent"
            (linkedItemsEvent)="handleLinkedItems($event)"
          ></app-link-item>
        </div>
        <div>
          <app-items-attachments
            [itemId]="item?.id"
            [startDateForRecurringUpdate]="isRecurringUpdateAllowed() ? (item?.dueDate ?? null) : null"
            [boardId]="item?.boardId"
            [loadStatus]="attachmentLoadStatusFromComponent"
            (memoryStoredAttachments)="onMemoryStoredAttachmentsChange($event)"
            (hasOngoingProcessChange)="onAttachmentOngoingProcessChange($event)"
            [externalSelectedFiles]="selectedFileChangeEvent"
            (rulesChange)="onAttachmentRulesChange($event)"
            [capturedPhotoFile]="capturedPhotoFile"
            (deviceCameraAvailabilityChange)="onDeviceCameraAvailabilityChange($event)"
            (maxFilesCountExceededChange)="onMaxFilesCountExceededChange($event)"
          ></app-items-attachments>
        </div>
        <hr *ngIf="item" class="my-4" />
        <button
          *ngIf="item"
          class="mb-3"
          [style]="{ color: isShowComments ? '#448aff' : '' }"
          mat-button
          type="button"
          (click)="onClickCommentButton()"
        >
          {{ 'button.comments' | translate }}
        </button>
        <button
          *ngIf="item"
          class="mb-3 history-button"
          [style]="{ color: !isShowComments ? '#448aff' : '' }"
          mat-button
          type="button"
          (click)="onClickHistoryButton()"
        >
          {{ 'button.history' | translate }}
        </button>
        <app-item-comment *ngIf="item && isShowComments" [item]="item"></app-item-comment>
        <app-items-history *ngIf="item && !isShowComments" [item]="item"></app-items-history>
      </div>
      <div class="col-lg-3 col-md-12">
        <div class="row">
          <div class="12 mb-1">
            <mat-dropdown
              [formControl]="addEditForm.controls.boardId"
              [configurationInput]="{
                hasDelayedDefaultValue: true,
                singleSelection: true,
                text: ('field.board' | translate),
                repositionDropdownList: true,
                hasMatError: !item?.escalatedBoardId,
              }"
              [options]="store.boardOptions()"
              (search)="searchBoards($event)"
              [isFormSubmitted]="form.submitted"
            ></mat-dropdown>
            <div class="escalated-board-info d-flex align-items-center" *ngIf="item?.escalatedBoardId">
              <mat-icon class="escalated-board-icon">arrow_upward</mat-icon>
              <span class="f-12 ms-1">{{ 'page.items.escalation.escalatedTo' | translate }}:</span>
              <div class="board d-flex ms-1">
                <mat-icon>grid_on</mat-icon>
                <span class="f-12 ms-1">{{ item?.escalatedBoard?.name }}</span>
              </div>
            </div>
          </div>
          <div class="12 mb-1">
            <mat-dropdown
              [formControl]="addEditForm.controls.itemType"
              [configurationInput]="{
                hasDelayedDefaultValue: true,
                isClientSide: true,
                singleSelection: true,
                text: ('field.itemType' | translate),
                repositionDropdownList: true,
              }"
              [options]="itemTypeOptions"
              [isFormSubmitted]="form.submitted"
            ></mat-dropdown>
          </div>
          <div class="12 mb-1">
            <mat-dropdown
              [formControl]="addEditForm.controls.itemCategory"
              [configurationInput]="{
                hasDelayedDefaultValue: true,
                isClientSide: true,
                singleSelection: true,
                text: ('field.itemCategory' | translate),
                repositionDropdownList: true,
              }"
              [options]="itemCategoryOptions$()"
              [isFormSubmitted]="form.submitted"
            ></mat-dropdown>
          </div>
          <div class="12 mb-1">
            <mat-dropdown
              [formControl]="addEditForm.controls.status"
              [configurationInput]="{
                hasDelayedDefaultValue: true,
                isClientSide: true,
                singleSelection: true,
                text: ('field.status' | translate),
                repositionDropdownList: true,
              }"
              [options]="workflowStepOptions"
              [isFormSubmitted]="form.submitted"
            ></mat-dropdown>
          </div>
          <div class="12 mb-1">
            <mat-dropdown
              [formControl]="addEditForm.controls.assignee"
              [configurationInput]="{
                hasDelayedDefaultValue: true,
                placeholder: ('page.items.label.unassigned' | translate),
                singleSelection: true,
                text: ('field.assignee' | translate),
                repositionDropdownList: true,
              }"
              [options]="selectedBoardId ? store.boardMembers() : []"
              (search)="searchBoardMembers($event, $event === '' ? item?.assigneeId : undefined)"
              [isFormSubmitted]="form.submitted"
            ></mat-dropdown>
          </div>
          <div class="12 date-range-picker-container">
            <app-date-range-picker
              [formControl]="addEditForm.controls.dueDate"
              [(dates)]="dueDate"
              [configurationInput]="{
                drops: 'up',
                label: ('field.dueDate' | translate),
                opens: 'center',
                showClearButton: true,
                singleDatePicker: true,
                showErrors: !!item?.itemRecurrenceId || (cronToString$ | async) === null,
              }"
              [isFormSubmitted]="form.submitted"
            ></app-date-range-picker>
          </div>

          @if (!item?.itemRecurrenceId && !!(cronToString$ | async)) {
            <mat-hint class="text-grey pt-1 pb-2 ps-4 mb-1 d-block f-12">{{ recurringItemDueDateHint }} </mat-hint>
          }

          <div class="12 mt-1">
            <mat-dropdown
              [formControl]="addEditForm.controls.reporter"
              [configurationInput]="{
                text: ('field.reporter' | translate),
                singleSelection: true,
                repositionDropdownList: true,
              }"
              [isFormSubmitted]="form.submitted"
            ></mat-dropdown>
          </div>
          <div class="mb-3">
            <p
              class="mt-0 mb-2 m f-12 mat-field-label-color"
              *ngIf="item?.createdAt"
              [matTooltip]="item?.createdAt ?? null | utcToUserFormatted: true"
            >
              {{ 'field.created' | translate: { date: item?.createdAt | relativeTime: userLanguage } }}
            </p>
            <p
              class="mt-0 f-12 mat-field-label-color"
              *ngIf="item?.changedAt"
              [matTooltip]="item?.changedAt ?? null | utcToUserFormatted: true"
            >
              {{ 'field.updated' | translate: { date: item?.changedAt | relativeTime: userLanguage } }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
  <mat-dialog-actions align="end">
    @if (addEditForm && addEditForm.isThereUnsavedChanges()) {
      <button mat-button type="button" (click)="doCancelConfirmation()" [disabled]="isSubmitInProgress$ | async">
        {{ 'button.cancel' | translate }}
      </button>
      <button mat-flat-button type="submit" [disabled]="isSubmitInProgress$ | async">
        {{ data.submitButtonText }}
      </button>
    } @else {
      <button mat-button mat-dialog-close>
        {{ 'button.close' | translate }}
      </button>
    }
  </mat-dialog-actions>
</form>

<mat-menu #addAttachmentsMenu="matMenu">
  <button mat-menu-item (click)="fileInput.click()" type="button">
    <mat-icon>folder_open</mat-icon>
    <span>{{ 'button.uploadFiles' | translate }}</span>
    <input
      #fileInput
      multiple
      type="file"
      (change)="onFileChange($event, fileInput)"
      [accept]="attachmentRules.allowedUploadableFileMimes.join(',')"
      [disabled]="isMaxFilesCountExceeded"
      style="display: none"
    />
  </button>
  <button
    mat-menu-item
    (click)="openCamera()"
    [disabled]="!isDeviceCameraAvailable || isMaxFilesCountExceeded"
    type="button"
  >
    <mat-icon>photo_camera</mat-icon>
    <span>{{ 'button.takePhoto' | translate }}</span>
  </button>
</mat-menu>

<mat-menu #menu="matMenu">
  <button
    mat-menu-item
    type="button"
    [matMenuTriggerFor]="addAttachmentsMenu"
    [disabled]="isMaxFilesCountExceeded || hasAttachmentComponentOngoingProcess || (isSubmitInProgress$ | async)"
    [autofocus]="false"
    tabindex="-1"
  >
    <mat-icon>attach_file</mat-icon>
    {{ 'button.attachFile' | translate }}
  </button>
  <button
    mat-menu-item
    [disabled]="isSubmitInProgress$ | async"
    (click)="initializeLinkItem()"
    type="button"
    tabindex="-1"
  >
    <mat-icon>link</mat-icon>
    {{ 'button.link' | translate }}
  </button>
  @if (item) {
    <button mat-menu-item (click)="openWorkInstructionList()">
      <mat-icon>menu_book</mat-icon>
      {{ 'field.workInstruction' | translate }}
    </button>
  }
  @if ((store.isDataForEditLoading$ | async) === false) {
    <button
      mat-menu-item
      [disabled]="(isSubmitInProgress$ | async) || (!item?.itemRecurrenceId && item?.escalatedBoardId)"
      (click)="openRecurrenceEditModalIfEnabled()"
      type="button"
      tabindex="-1"
    >
      <mat-icon>repeat</mat-icon>
      {{ (item?.itemRecurrenceId ? 'button.showRecurrence' : 'button.manageRecurrence') | translate }}
    </button>
  }
</mat-menu>
